import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { organizationSettingsApi } from '../services/api';
import { OrganizationSettings } from '../types';
import { useAuth } from '../auth/AuthContext';

interface SettingsContextType {
  settings: OrganizationSettings['settings'] | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<OrganizationSettings['settings'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { loading: authLoading, user } = useAuth();

  const loadSettings = async () => {
    const orgIdStr = localStorage.getItem('selectedOrganizationId');
    if (!orgIdStr) {
      setSettings(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const orgId = parseInt(orgIdStr);
      
      // Retry on network errors
      let settingsData;
      try {
        settingsData = await organizationSettingsApi.get(orgId);
      } catch (err: any) {
        // Retry once after a delay if it's a network error
        if (err.code === 'ERR_NETWORK' || err.code === 'ERR_CONNECTION_REFUSED') {
          await new Promise(resolve => setTimeout(resolve, 1000));
          settingsData = await organizationSettingsApi.get(orgId);
        } else {
          throw err;
        }
      }
      
      setSettings(settingsData.settings);
    } catch (err) {
      console.error('Failed to load organization settings:', err);
      setError('Failed to load settings');
      // Set default settings if loading fails
      setSettings({
        general: {
          default_batch_size: 10,
          max_test_cases_per_generation: 50,
          evaluation_timeout_seconds: 30
        },
        display: {
          dark_mode: false,
          show_notifications: true,
          auto_save: true
        },
        api: {
          openai_api_key: ''
        },
        integrations: {
          webhook_enabled: false,
          database_sync_enabled: true,
          email_notifications_enabled: false
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    await loadSettings();
  };

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user) {
      setSettings(null);
      setLoading(false);
      setError(null);
      return;
    }

    const timer = setTimeout(() => {
      loadSettings();
    }, 200);

    const handleOrganizationChange = () => {
      loadSettings();
    };

    window.addEventListener('organizationChanged', handleOrganizationChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('organizationChanged', handleOrganizationChange);
    };
  }, [authLoading, user]);

  return (
    <SettingsContext.Provider value={{ settings, loading, error, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Helper hook for specific setting values
export const useSetting = <T,>(key: string, defaultValue: T): T => {
  const { settings } = useSettings();
  
  if (!settings) {
    return defaultValue;
  }

  const keys = key.split('.');
  let value: any = settings;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return defaultValue;
    }
  }
  
  return value as T;
};
